import { forwardRef, type InputHTMLAttributes } from 'react';
import { Text } from 'components/text/primary/Text';
import { cn } from 'utils/helpers';

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  className?: string;
  required?: boolean;
  error?: boolean | string;
  endAdornment?: React.ReactNode;
  icon?: React.ReactNode; // Added icon prop
}

const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    { label, className, required, error, endAdornment, icon, ...props },
    ref,
  ) => {
    // Destructured icon prop
    return (
      <div className="relative flex flex-col w-full gap-2">
        <div className="flex justify-between items-center gap-2">
          <label className="text-[0.875rem]">{label}</label>
          {required && <div>*</div>}
        </div>
        <div className="flex justify-start items-center relative">
          {icon && <div className="left-4 absolute">{icon}</div>}{' '}
          <input
            type="text"
            className={cn(
              'w-full p-4 bg-[#32395E] outline-none rounded-[1rem] transition-all duration-300 focus:bg-[#465184]',
              className,
              `${error ? 'border-red-600' : 'border-slate-600 border-opacity-0'}`,
              icon && 'pl-[2.625rem]',
            )}
            ref={ref}
            required={required}
            autoComplete="off"
            {...props}
          />
          {endAdornment && (
            <div className="absolute right-4 h-full flex items-center">
              {endAdornment}
            </div>
          )}
        </div>
        {error && (
          <Text size="extra-small" type="error" className="mt-1.5">
            {error}
          </Text>
        )}
      </div>
    );
  },
);

TextField.displayName = 'TextField';

export default TextField;
